import React, { useState } from 'react';
import {
    Box,
    IconButton,
    Theme,
    Tooltip,
    Typography,
    useTheme,
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { Download, FileText, Film, Music, Play } from 'react-feather';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import type { TConversationMediaItem } from 'src/views/OmniChannel/utils/conversationMediaGallery';
import { downloadMediaFile } from 'src/views/OmniChannel/utils/conversationMediaGallery';

export const MediaGalleryItem = ({
    item,
    variant = 'grid',
    onOpen,
}: {
    item: TConversationMediaItem;
    variant?: 'grid' | 'list';
    onOpen: (item: TConversationMediaItem) => void;
}) => {
    const theme = useTheme<{
        palette: Theme['palette'] & { background: { dark: string } };
    }>();
    const { t } = useTranslation();
    const [isImageLoaded, setIsImageLoaded] = useState(false);

    const formattedDate = moment(item.createdAt).format('DD/MM/YYYY HH:mm');

    const handleDownload = (event: React.MouseEvent) => {
        event.stopPropagation();
        downloadMediaFile(item.source, item.name);
    };

    const renderDownloadButton = (overlay = false) => (
        <Tooltip title={t('Chat.MediaGalleryDownload')}>
            <IconButton
                size="small"
                onClick={handleDownload}
                style={
                    overlay
                        ? {
                              backgroundColor: 'rgba(0, 0, 0, 0.45)',
                              color: '#fff',
                          }
                        : undefined
                }
            >
                <Download size={16} />
            </IconButton>
        </Tooltip>
    );

    const actionButtons = (
        <Box display="flex" alignItems="center" gridGap={4}>
            {renderDownloadButton()}
        </Box>
    );

    const overlayActionButtons = (
        <Box display="flex" alignItems="center" gridGap={4}>
            {renderDownloadButton(true)}
        </Box>
    );

    if (variant === 'list') {
        const Icon =
            item.mediaType === 'audios'
                ? Music
                : item.mediaType === 'videos'
                  ? Film
                  : FileText;

        return (
            <Box
                onClick={() => onOpen(item)}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 14px',
                    borderRadius: 8,
                    cursor: 'pointer',
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Box
                    style={{
                        width: 40,
                        height: 40,
                        borderRadius: 8,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: theme.palette.action.hover,
                        flexShrink: 0,
                    }}
                >
                    <Icon size={18} />
                </Box>
                <Box style={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        variant="body2"
                        noWrap
                        title={item.name}
                        color="textPrimary"
                    >
                        {item.name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                        {formattedDate}
                    </Typography>
                </Box>
                {actionButtons}
            </Box>
        );
    }

    if (item.mediaType === 'videos') {
        return (
            <Box
                onClick={() => onOpen(item)}
                style={{
                    position: 'relative',
                    width: '100%',
                    height: 160,
                    borderRadius: 8,
                    overflow: 'hidden',
                    cursor: 'pointer',
                    backgroundColor: theme.palette.action.hover,
                    border: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Box
                    style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Film size={28} />
                </Box>
                <Box
                    style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(0, 0, 0, 0.25)',
                    }}
                >
                    <Play size={28} color="#fff" fill="#fff" />
                </Box>
                <Box
                    style={{
                        position: 'absolute',
                        top: 6,
                        right: 6,
                    }}
                    onClick={(event) => event.stopPropagation()}
                >
                    {overlayActionButtons}
                </Box>
                <Typography
                    variant="caption"
                    style={{
                        position: 'absolute',
                        left: 8,
                        bottom: 8,
                        color: '#fff',
                        textShadow: '0 1px 2px rgba(0,0,0,0.6)',
                    }}
                    noWrap
                >
                    {formattedDate}
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            style={{
                position: 'relative',
                width: '100%',
                height: 160,
                borderRadius: 8,
                overflow: 'hidden',
                cursor: 'pointer',
                border: `1px solid ${theme.palette.divider}`,
            }}
            onClick={() => onOpen(item)}
        >
            {!isImageLoaded && (
                <Skeleton
                    variant="rect"
                    animation="wave"
                    style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                    }}
                />
            )}
            <img
                src={item.source}
                alt={item.name}
                onLoad={() => setIsImageLoaded(true)}
                style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    opacity: isImageLoaded ? 1 : 0,
                    transition: 'opacity 0.2s ease',
                }}
            />
            <Box
                style={{
                    position: 'absolute',
                    top: 6,
                    right: 6,
                }}
                onClick={(event) => event.stopPropagation()}
            >
                {overlayActionButtons}
            </Box>
            <Typography
                variant="caption"
                style={{
                    position: 'absolute',
                    left: 8,
                    bottom: 8,
                    color: '#fff',
                    textShadow: '0 1px 2px rgba(0,0,0,0.6)',
                }}
            >
                {formattedDate}
            </Typography>
        </Box>
    );
};
